/**
 * Per-customer agent ROI tile for /admin/agent-economics.
 *
 * Drops in below the fleet summary. Consumes /api/agent-economics/customer-rollup
 * + /api/agent-economics/customer-tier-budgets so operators can see WHICH
 * customers are profitable vs losing money, and CARTIE's recommended hard-cap
 * per customer.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Users, Trophy, Skull, Gauge, TrendingDown, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerdictPill = ({ verdict }) => {
  const map = {
    PROFITABLE: { cls: 'bg-emerald-500/15 text-emerald-300', icon: Trophy },
    BREAK_EVEN: { cls: 'bg-amber-500/15 text-amber-300', icon: Gauge },
    LOSS_MAKING: { cls: 'bg-rose-500/15 text-rose-300', icon: Skull },
    NO_OUTCOME: { cls: 'bg-slate-500/15 text-slate-300', icon: Gauge },
  };
  const m = map[verdict] || map.NO_OUTCOME;
  const Icon = m.icon;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${m.cls}`}><Icon className="w-2.5 h-2.5" /> {verdict}</span>;
};

export default function CustomerROITile() {
  const [rollup, setRollup] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const h = { Authorization: `Bearer ${token}` };
      const [rRes, tRes] = await Promise.all([
        fetch(`${API}/agent-economics/customer-rollup?days=30`, { headers: h }),
        fetch(`${API}/agent-economics/customer-tier-budgets?days=30`, { headers: h }),
      ]);
      if (rRes.ok) setRollup(await rRes.json());
      if (tRes.ok) setTiers((await tRes.json()).recommendations || []);
    } finally { setLoaded(true); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  if (!loaded) {
    return <Card className="bg-slate-950 border-slate-800/60 animate-pulse" data-testid="cust-roi-loading"><CardContent className="p-6 h-32" /></Card>;
  }
  if (!rollup || (rollup.customers || []).length === 0) {
    return (
      <Card className="bg-slate-950 border-emerald-500/20" data-testid="cust-roi-empty">
        <CardContent className="p-5 text-center">
          <Users className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-400">No customer-attributed agent runs yet. Pass <code className="text-cyan-300">customer_id</code> when starting an agent run to enable per-customer ROI.</p>
        </CardContent>
      </Card>
    );
  }

  const customers = rollup.customers || [];
  const tierByCustomer = Object.fromEntries(tiers.map(t => [t.customer_id, t]));

  return (
    <Card className="bg-slate-950 border-emerald-400/30" data-testid="cust-roi-tile">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Users className="w-4 h-4 text-emerald-300" />
            Per-customer agent ROI
            <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 text-[9px] font-mono tracking-widest">{rollup.window_days}D</Badge>
          </CardTitle>
          <div className="text-xs text-slate-300">
            Fleet ROI: <span className="text-emerald-300 font-bold">{rollup.fleet_roi_multiplier ? `${rollup.fleet_roi_multiplier}×` : '—'}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Headline stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="rounded-md bg-slate-950 border border-slate-700 p-3">
            <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-1">Customers</div>
            <div className="text-2xl font-bold text-white tabular-nums">{rollup.customer_count}</div>
          </div>
          <div className="rounded-md bg-gradient-to-br from-emerald-500/15 to-slate-900/60 border border-emerald-400/30 p-3">
            <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-1">Profitable</div>
            <div className="text-2xl font-bold text-emerald-200 tabular-nums">{rollup.profitable_customer_count}</div>
          </div>
          <div className="rounded-md bg-gradient-to-br from-rose-500/15 to-slate-900/60 border border-rose-400/30 p-3">
            <div className="text-[10px] font-bold text-rose-300 uppercase tracking-widest mb-1">Loss-making</div>
            <div className="text-2xl font-bold text-rose-200 tabular-nums">{rollup.loss_making_customer_count}</div>
          </div>
          <div className="rounded-md bg-slate-950 border border-slate-700 p-3">
            <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-1">Outcome / Cost</div>
            <div className="text-xl font-bold text-white tabular-nums">${rollup.total_outcome_usd} / ${rollup.total_cost_usd}</div>
          </div>
        </div>

        {/* Customer table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase text-cyan-300 border-b border-slate-800">
              <tr>
                <th className="text-left py-2">Customer</th>
                <th className="text-right">Runs</th>
                <th className="text-right">Cost</th>
                <th className="text-right">Outcome</th>
                <th className="text-right">ROI</th>
                <th className="text-right">Runaway</th>
                <th className="text-right">Verdict</th>
                <th className="text-right">Recommended cap</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {customers.map((c, i) => {
                const tier = tierByCustomer[c.customer_id];
                return (
                  <tr key={i} className="hover:bg-slate-800" data-testid={`cust-row-${i}`}>
                    <td className="py-2 text-white font-mono text-xs truncate max-w-[200px]">{c.customer_id}</td>
                    <td className="py-2 text-right text-slate-400">{c.run_count}</td>
                    <td className="py-2 text-right font-mono text-slate-300">${c.total_cost_usd}</td>
                    <td className="py-2 text-right font-mono text-emerald-300">${c.total_outcome_usd}</td>
                    <td className="py-2 text-right font-bold text-white">{c.roi_multiplier !== null ? `${c.roi_multiplier}×` : '—'}</td>
                    <td className="py-2 text-right">
                      {c.runaway_count > 0
                        ? <span className="text-rose-300 text-xs">{c.runaway_count} ({c.runaway_rate_pct}%)</span>
                        : <span className="text-slate-500">—</span>}
                    </td>
                    <td className="py-2 text-right"><VerdictPill verdict={c.verdict} /></td>
                    <td className="py-2 text-right">
                      {tier ? (
                        <span title={tier.rationale} className="text-cyan-300 font-mono cursor-help">${tier.recommended_hard_cap_usd}</span>
                      ) : <span className="text-slate-500">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="text-[11px] text-slate-300 italic mt-3 flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Recommended caps auto-adjust based on per-customer ROI — LOSS_MAKING customers get aggressive caps. Hover for rationale.
        </div>
      </CardContent>
    </Card>
  );
}
